import { useEffect, useRef } from "react";
import {
  selectIsAssessmentActive,
  useAssessmentStore,
} from "@/features/tutor/store/useAssessmentStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";

export const LEAVE_GUARD_STATE_KEY = "lessonLeaveGuard";

let lastEntryToken = 0;

const hasUserActivation = () =>
  navigator.userActivation ? navigator.userActivation.hasBeenActive : true;

const ACTIVATION_EVENTS = [
  "pointerdown",
  "pointerup",
  "mousedown",
  "touchend",
  "keydown",
];

export function useLessonLeaveGuard() {
  const sessionId = useSessionStore((s) => s.sessionId);
  const lessonExitCommitted = useUIStore((s) => s.lessonExitCommitted);
  const armed = useUIStore((s) => s.lessonLeaveGuardArmed);
  const setArmed = useUIStore((s) => s.setLessonLeaveGuardArmed);
  const active = Boolean(sessionId) && !lessonExitCommitted;
  const entryTokenRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const handleBeforeUnload = (event) => {
      if (useUIStore.getState().lessonExitCommitted) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [active]);

  useEffect(() => {
    if (!active) {
      if (armed) setArmed(false);
      return undefined;
    }
    if (armed) return undefined;

    const arm = () => {
      if (useUIStore.getState().lessonLeaveGuardArmed) return;
      const { state } = window.history;
      const token = ++lastEntryToken;
      window.history.replaceState(
        { ...state, [LEAVE_GUARD_STATE_KEY]: token },
        "",
      );
      window.history.pushState(state, "", window.location.href);
      entryTokenRef.current = token;
      setArmed(true);
    };

    if (hasUserActivation()) {
      arm();
      return undefined;
    }

    const stopListening = () =>
      ACTIVATION_EVENTS.forEach((type) =>
        window.removeEventListener(type, handleActivation, true),
      );
    const handleActivation = () => {
      if (!hasUserActivation()) return;
      stopListening();
      arm();
    };
    ACTIVATION_EVENTS.forEach((type) =>
      window.addEventListener(type, handleActivation, true),
    );
    return stopListening;
  }, [active, armed, setArmed]);

  useEffect(() => {
    if (!armed) return undefined;
    const handlePopState = (event) => {
      const token = entryTokenRef.current;
      if (!token || event.state?.[LEAVE_GUARD_STATE_KEY] !== token) return;
      window.history.go(1);
      const ui = useUIStore.getState();
      if (
        ui.showExitLessonModal ||
        ui.showLessonCompleteModal ||
        selectIsAssessmentActive(useAssessmentStore.getState())
      ) {
        return;
      }
      ui.setShowExitLessonModal(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [armed]);

  useEffect(
    () => () => useUIStore.getState().setLessonLeaveGuardArmed(false),
    [],
  );
}
